import { useCallback, useRef } from 'react';
import { TimeUpdateData } from '../types';

interface EventHandlers {
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
  onLoadStart?: () => void;
  onCanPlay?: () => void;
  onTimeUpdate?: (timeData: TimeUpdateData) => void;
}

export const useEventHandlers = (
  videoRef: React.RefObject<HTMLVideoElement>,
  playerRef: React.RefObject<any>,
  handlers: EventHandlers
) => {
  const timeUpdateHandlerRef = useRef<(() => void) | null>(null);

  const setupEventListeners = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    const { onPlay, onPause, onEnded, onLoadStart, onCanPlay, onTimeUpdate } = handlers;

    if (onPlay) video.addEventListener('play', onPlay);
    if (onPause) video.addEventListener('pause', onPause);
    if (onEnded) video.addEventListener('ended', onEnded);
    if (onLoadStart) video.addEventListener('loadstart', onLoadStart);
    if (onCanPlay) video.addEventListener('canplay', onCanPlay);
    
    // Handle timeupdate with comprehensive data
    if (onTimeUpdate) {
      const handleTimeUpdate = () => {
        const player = playerRef.current;
        const currentTime = video.currentTime;
        const duration = video.duration;
        
        // Check if it's a live stream
        const isLive = player && typeof player.isLive === 'function' ? player.isLive() : false;
        
        const timeData: TimeUpdateData = {
          currentTime,
          duration,
          isLive
        };
        
        if (isLive && player && typeof player.seekRange === 'function') {
          // For live streams, provide additional data
          const seekRange = player.seekRange();
          if (seekRange && seekRange.end !== undefined) {
            timeData.liveEdge = seekRange.end;
            timeData.timeBehindLive = seekRange.end - currentTime;
          }
        } else if (duration && isFinite(duration) && duration > 0) {
          // For regular videos, provide progress percentage
          timeData.progress = (currentTime / duration) * 100;
        }
        
        onTimeUpdate(timeData);
      };
      
      timeUpdateHandlerRef.current = handleTimeUpdate;
      video.addEventListener('timeupdate', handleTimeUpdate);
    }
  }, [videoRef, playerRef, handlers]);

  const cleanupEventListeners = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    const { onPlay, onPause, onEnded, onLoadStart, onCanPlay } = handlers;

    if (onPlay) video.removeEventListener('play', onPlay);
    if (onPause) video.removeEventListener('pause', onPause);
    if (onEnded) video.removeEventListener('ended', onEnded);
    if (onLoadStart) video.removeEventListener('loadstart', onLoadStart);
    if (onCanPlay) video.removeEventListener('canplay', onCanPlay);
    
    if (timeUpdateHandlerRef.current) {
      video.removeEventListener('timeupdate', timeUpdateHandlerRef.current);
      timeUpdateHandlerRef.current = null;
    }
  }, [videoRef, handlers]);

  return {
    setupEventListeners,
    cleanupEventListeners
  };
}; 